// IndexedDB cache for converted IFC fragments. The IFC→fragments conversion
// is the 3D preview's long pole (minutes of single-threaded wasm on big
// models) and is pure per input bytes — caching the output makes every
// re-open of the same file instant, in the importer AND the document viewer.
//
// Bump STORE when the @thatopen/fragments serialization format changes — old
// entries are simply never read again and age out via the LRU cap.
const DB_NAME = 'ifc-fragment-cache';
const STORE = 'fragments-v1';
const MAX_ENTRIES = 10;

interface CacheRow {
    key: string;
    bytes: Uint8Array;
    at: number;
}

function openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(STORE)) {
                req.result.createObjectStore(STORE, { keyPath: 'key' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function tx<T>(db: IDBDatabase, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

export const FragmentCache = {
    async get(key: string): Promise<Uint8Array | null> {
        const db = await openDb();
        try {
            const row = (await tx<CacheRow | undefined>(db, 'readonly', (s) => s.get(key) as IDBRequest<CacheRow | undefined>)) ?? null;
            if (!row) return null;
            // Refresh recency (best-effort) so the LRU cap keeps hot entries.
            tx(db, 'readwrite', (s) => s.put({ ...row, at: Date.now() })).catch(() => {});
            return row.bytes;
        } finally {
            db.close();
        }
    },

    async put(key: string, bytes: Uint8Array): Promise<void> {
        const db = await openDb();
        try {
            await tx(db, 'readwrite', (s) => s.put({ key, bytes, at: Date.now() } satisfies CacheRow));
            // LRU cap: drop the oldest entries beyond MAX_ENTRIES.
            const rows = (await tx<CacheRow[]>(db, 'readonly', (s) => s.getAll() as IDBRequest<CacheRow[]>)) ?? [];
            if (rows.length > MAX_ENTRIES) {
                const stale = rows.sort((a, b) => a.at - b.at).slice(0, rows.length - MAX_ENTRIES);
                for (const row of stale) {
                    await tx(db, 'readwrite', (s) => s.delete(row.key)).catch(() => {});
                }
            }
        } finally {
            db.close();
        }
    },
};
